import {
  Popover as ChakraPopover,
  PopoverAnchor as ChakraPopoverAnchor,
  PopoverContent as ChakraPopoverContent,
  PopoverProps as ChakraPopoverProps,
  PopoverTrigger as ChakraPopoverTrigger,
  Divider,
  Portal,
} from "@chakra-ui/react"
import React from "react"

type WhitelistedChakraPopoverProps = Pick<
  ChakraPopoverProps,
  | "autoFocus"
  | "boundary"
  | "closeDelay"
  | "closeOnBlur"
  | "closeOnEsc"
  | "computePositionOnMount"
  | "defaultIsOpen"
  | "eventListeners"
  | "gutter"
  | "id"
  | "initialFocusRef"
  | "isLazy"
  | "isOpen"
  | "lazyBehavior"
  | "modifiers"
  | "offset"
  | "onClose"
  | "onOpen"
  | "openDelay"
  | "placement"
  | "returnFocusOnClose"
  | "strategy"
  | "trigger"
>

const SIDE_PADDING = "1rem"

const SIZES = {
  small: "18.75rem",
  large: "25rem",
} as const
type Size = keyof typeof SIZES

type PopoverRootProps = React.PropsWithChildren<WhitelistedChakraPopoverProps>

const PopoverRoot = React.forwardRef<HTMLDivElement, PopoverRootProps>(
  ({ children, ...props }, ref) => {
    return (
      <ChakraPopover gutter={8} ref={ref} {...props}>
        {children}
      </ChakraPopover>
    )
  }
)

PopoverRoot.displayName = "PopoverRoot"

type PopoverContentProps = React.PropsWithChildren<{
  size?: Size
  isInPortal?: boolean
  zIndex?: number
  matchWidth?: boolean
}>

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      size = "small",
      matchWidth,
      isInPortal = true,
      zIndex,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <MaybePortal isInPortal={isInPortal}>
        <ChakraPopoverContent
          _focusVisible={{
            layerStyle: "focused",
          }}
          maxHeight={`var(--popover-max-height)`}
          overflowY="auto"
          {...props}
          ref={ref}
          w={matchWidth ? "inherit" : SIZES[size]}
          p={SIDE_PADDING}
          border="none"
          borderRadius="16px"
          shadow="ds.overlay"
          zIndex={zIndex}
          background="ds.surface.overlay.default"
          sx={{
            "--popover-max-height": "90vh",
            "--popover-padding": SIDE_PADDING,
          }}
        >
          {children}
        </ChakraPopoverContent>
      </MaybePortal>
    )
  }
)

PopoverContent.displayName = "PopoverContent"

const MaybePortal: React.FC<
  React.PropsWithChildren<{ isInPortal: boolean }>
> = ({ isInPortal, children }) => {
  return isInPortal ? <Portal>{children}</Portal> : children
}

const PopoverDivider: React.FC = () => {
  return (
    <Divider
      w={`calc(100% + 2 * ${SIDE_PADDING})`}
      mx={`-${SIDE_PADDING}`}
      opacity={1}
      borderColor="ds.border.default"
    />
  )
}

/**
 * ### Example
 * ```
 * <Popover.Root placement="bottom-start" trigger="hover">
 *   <Popover.Trigger>
 *     <Button>Open Popover</Button>
 *   </Popover.Trigger>
 *   <Popover.Content size="small">
 *     Popover content goes here
 *   </Popover.Content>
 * </Popover.Root>
 * ```
 *
 * ### Autofocus behaviour
 *
 * Use the `initialFocusRef` prop on the `Root` component to autofocus the
 * popover when it opens. Check the `PopoverWithAutoFocus` story in Ladle for
 * an example.
 *
 * ### Root
 *
 * This should wrap both the trigger and the content.
 *
 * A {@link WhitelistedChakraPopoverProps | large list} of props (such as `isLazy`) are
 * available to use on the `Root` component for fine-grained control in edge cases – see
 * the {@link https://v2.chakra-ui.com/docs/components/popover/usage | Chakra usage docs}
 * for more info.
 *
 * ### Content
 *
 * The content of the popover.
 *
 * We wrap the `Popover.Content`'s children in a `<Portal>` by default since
 * it's such a common use case (and frustrating if you don't know about it and
 * need it!) but it can be overridden by doing `isInPortal={false}`.
 *
 * @param size - `"small" | "large"`
 * @param isInPortal - Whether the content should be rendered in a portal (`true` by default).
 * @param zIndex - The z-index of the content. Don't use this unless you need to.
 * @param matchWidth - Whether the popover should match the width of the
 * trigger. Useful for some edge cases.
 *
 * ### Anchor
 *
 * To make the popover appear next to something other than the trigger, use the
 * `Anchor` component. It works like the Chakra one – see
 * {@link https://v2.chakra-ui.com/docs/components/popover/usage#popover-anchor | Chakra Popover Anchor docs}.
 *
 * ## Divider
 *
 * Standard divider that will bleed to the edges of the popover (through its padding).
 **/
export const Popover = {
  Root: PopoverRoot,
  Trigger: ChakraPopoverTrigger,
  Content: PopoverContent,
  Anchor: ChakraPopoverAnchor,
  Divider: PopoverDivider,
}
